import { FC, useState } from 'react';
import PropTypes from 'prop-types';
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Button, Divider, Grid, InputAdornment, TextField, Typography, } from '@material-ui/core';
import * as Yup from 'yup';
import { InputField } from 'src/components/input-field';
import { phoneRegExp } from 'src/components/common/common-regex';
import { useFormik } from 'formik';
import { userApi } from 'src/api/user';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

interface MobileNumberProps {
  getUserDetails: (data: string) => void
  userMobile?: string;
}

export const MobileNumberCard: FC<MobileNumberProps> = (props) => {
  const { getUserDetails, userMobile } = props;
  const [customersList, setCustomersList] = useState<Array<any>>([]);
  const [searchExpanded, setSearchExpanded] = useState<boolean>(true);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      mobile: userMobile ? userMobile.slice(3) : '',
      name: '',
      username: '',
      submit: null
    },
    validationSchema: Yup.object().shape({
      mobile: Yup.string().matches(phoneRegExp, 'Mobile Number is not valid'),
      name: Yup.string()
    }),

    onSubmit: async (values) => {
      getUserDetails(`+91${values.mobile}`);
      setSearchExpanded(false);
    }
  });

  const getCustomersList = async (customerName: string) => {
    await userApi.getCustomersListByName(customerName)
      .then((response) => {
        setCustomersList(response?.responseData);
      })
      .catch((err) => console.error(err));
  };

  const handleAutoCompleteInput = (event, newValue) => {
    if (newValue?.username) {
      formik.setFieldValue('username', newValue?.username);
      getUserDetails(newValue?.username);
      setSearchExpanded(false);
    }
  };

  const handleNameInput = (event) => {
    // reset mobile number field
    if (formik.values.mobile) {
      formik.setFieldValue('mobile', '');
    }
    if (event.target.value?.length >= 3) {
      getCustomersList(event.target.value);
    } else {
      setCustomersList([]);
    }
  };

  const handleMobileNumberInput = (event) => {
    formik.setFieldValue('mobile', event.target.value);

    // reset customer name field
    if (formik.values.username) {
      formik.setFieldValue('username', '');
    }
  };

  const renderLabel = (label: string) => (
    <Grid
      xs={5}
      item
    >
      <Typography
        variant="subtitle2"
        sx={{
          mt: '6px',
        }}
      >
        {label}
      </Typography>

    </Grid>
  );

  return (
    <Accordion
      variant="outlined"
      defaultExpanded
      disableGutters
      expanded={searchExpanded}
      sx={{ m: 0 }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        onClick={() => setSearchExpanded((prevState) => !prevState)}
        sx={{
          backgroundColor: 'neutral.100'
        }}
      >
        <Typography
          variant="h6"
        >
          Search Customer
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid
          container
          direction="column"
          xs={12}
          sm={10}
          md={8}
          lg={5}
        >
          <Grid
            container
            direction="row"
            paddingY={1}
          >
            {renderLabel('Mobile Number')}
            <Grid
              item
              xs={7}
              minWidth={30}
            >
              <InputField
                error={Boolean(formik.touched.mobile
                    && formik.errors.mobile)}
                helperText={formik.touched.mobile && formik.errors.mobile}
                fullWidth
                type="number"
                name="mobile"
                onChange={handleMobileNumberInput}
                onBlur={formik.handleBlur}
                placeholder="Mobile Number"
                value={formik.values.mobile}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                    >
                      +91
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            direction="row"
          >
            <Box
              sx={{
                width: '100%'
              }}
            >
              <Divider textAlign="center">
                <Typography
                  sx={{
                    p: 1,
                  }}
                  variant="h5"
                >
                  OR
                </Typography>
              </Divider>
            </Box>
          </Grid>
          <Grid
            container
            direction="row"
            paddingY={1}
          >
            {renderLabel('Name')}
            <Grid
              item
              xs={7}
              minWidth={30}
            >
              <Autocomplete
                freeSolo
                onChange={handleAutoCompleteInput}
                options={customersList}
                getOptionLabel={(option) => (option?.username ? `${option.username?.split('-')?.pop()?.toString()?.slice(-4)
                  ?.toUpperCase()}-${option.firstName} ${option.lastName}` : option)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    onChange={handleNameInput}
                    placeholder="Customer Name"
                    size="small"
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid
            container
            xs={12}
            justifyContent="flex-end"
            pt={2}
          >
            <Button
              variant="contained"
              disabled={!(formik.isValid && formik.dirty) || formik.isSubmitting}
              onClick={() => { formik.handleSubmit(); }}
            >
              Get Details
            </Button>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

MobileNumberCard.propTypes = {
  getUserDetails: PropTypes.func,
  userMobile: PropTypes.string
};
